import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { Button, Loader, Modal, Table } from '@itds/elements/index.js';
import { ZeroState } from '@itds/components/index.js';
import { QUOTE_ACTIONS } from '@itds/Constants';
import Toolbar from '@brokerage/components/Toolbar';
import { AgencyContext } from '@brokerage/contexts/AgencyContext';
import { Api } from '@brokerage/utils/Api';

import QuoteCustomActionsModal from '../components/QuoteCustomActionsModal';

const CustomActions = ({ mobile_app, setErrorMessage, setSuccessMessage }) => {
  const { agency } = useContext(AgencyContext);

  const [loading, setLoading] = useState(true);
  const [custom_actions, setCustomActions] = useState([]);
  const [modal, setModal] = useState(null);
  const [customActionDetails, setCustomActionDetails] = useState(null);

  useEffect(() => {
    loadCustomActions();
  }, []);

  const loadCustomActions = () => {
    setLoading(true);

    Api.loadAgencyMobileAppAAQCustomActions(mobile_app.guid).then(response => {
      setCustomActions(response.data);

      setLoading(false);
    });
  };

  const onHandleCustomActionClick = data => {
    setCustomActionDetails(data);
    setModal('edit');
  };

  const onSaveCustomAction = action => {
    action.mobile_app_guid = mobile_app.guid;

    Api.saveMobileAppAAQCustomAction(action.id, action)
      .then(() => {
        loadCustomActions();
        setModal(null);
        setSuccessMessage('Custom Action updated');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setModal(null);
        setErrorMessage('Unable to update the Custom Action at this time');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const onCloseModal = () => {
    setModal(null);
    setCustomActionDetails(null);
  };

  const onClickNewCustomAction = () => {
    setCustomActionDetails(null);
    setModal('edit');
  };

  const onHandleCustomActionDelete = (data, e) => {
    setCustomActionDetails(data);
    setModal('delete');

    e.stopPropagation();
  };

  const onDeleteConfirmPopup = () => {
    window.scrollTo(0, 0);

    Api.deleteMobileAppAAQCustomAction(customActionDetails.id)
      .then(() => {
        setModal(null);
        setCustomActionDetails(null);
        loadCustomActions();
        setSuccessMessage('Custom Action deleted');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setCustomActionDetails(null);
        setModal(null);
        setErrorMessage('Unable to delete the Custom Action at this time');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  const action_types = [
    {
      label: 'Basic',
      options: QUOTE_ACTIONS?.filter(item => item?.group === 'Basic')
    },
    {
      label: 'Defaults',
      options: QUOTE_ACTIONS?.filter(item => item?.group === 'Default')
    }
  ];

  return loading ? (
    <Loader />
  ) : (
    <>
      {modal === 'edit' ? (
        <QuoteCustomActionsModal
          actionTypes={action_types}
          agency={agency}
          availableCarrierIds={
            mobile_app?.annuity_carrier_ids ? mobile_app?.annuity_carrier_ids : []
          }
          availableProductIds={
            mobile_app?.annuity_product_ids ? mobile_app?.annuity_product_ids : []
          }
          beta={false} // TODO: pull this from quoter object
          hideIcon={true} // Don't know if we need this
          onClose={onCloseModal}
          onSave={onSaveCustomAction}
          quoteAction={customActionDetails}
          showOpenInModal={true} // Don't know if we need this
        />
      ) : null}

      {modal === 'delete' ? (
        <Modal
          buttons={[
            {
              color: 'RED',
              children: 'Confirm Delete',
              onClick: () => onDeleteConfirmPopup(),
              style: { marginLeft: 'auto' }
            }
          ]}
          contained={true}
          onClose={onCloseModal}
          title={`Delete "${customActionDetails?.label}"`}
        >
          <div style={{ padding: 40, fontSize: 16, textAlign: 'center', lineHeight: '1.5em' }}>
            Are you sure you want to delete the "{customActionDetails?.label}" Custom action?
          </div>
        </Modal>
      ) : null}

      <Toolbar
        buttons={[
          {
            label: 'New Custom Action',
            onClick: () => onClickNewCustomAction()
          }
        ]}
      />

      {custom_actions?.length ? (
        <Table
          columns={[
            {
              id: 'label',
              label: 'Label',
              render: data => {
                return <Link to={''}>{data?.label ? data?.label : 'N/A'}</Link>;
              }
            },
            {
              id: 'action_type',
              label: 'Type',
              render: data => {
                const quote_action = QUOTE_ACTIONS.find(item => item?.value === data?.action_type);
                const label = quote_action ? quote_action?.label : 'N/A';

                return <>{label}</>;
              }
            },
            {
              id: 'order',
              label: 'Order',
              render: data => {
                const order = data?.order ? data?.order : 'N/A';

                return <>{order}</>;
              }
            },
            {
              id: 'carriers',
              label: 'Carriers',
              render: data => {
                const carrier_count = data?.carrier_ids?.length;

                return <>{`${carrier_count} Carriers`}</>;
              }
            },
            {
              id: '',
              render: data => {
                return (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      color='RED'
                      onClick={onHandleCustomActionDelete.bind(null, data)}
                      small={true}
                    >
                      Delete
                    </Button>
                  </div>
                );
              }
            }
          ]}
          data={custom_actions}
          onRowClick={onHandleCustomActionClick}
        />
      ) : (
        <ZeroState
          icon='folder-open-outline'
          title="This Mobile AAQ doesn't have any Custom Actions"
        />
      )}
    </>
  );
};

CustomActions.propTypes = {
  mobile_app: PropTypes.object,
  setErrorMessage: PropTypes.func,
  setSuccessMessage: PropTypes.func
};

export default CustomActions;
